import React, { useEffect, useMemo, useRef, useState } from "react";
import { Offcanvas, Button } from "react-bootstrap";
import { CheckCircle } from "./PlaylistPublish/playlistShared";

/* ───────────── Shared expanded picker sheet (left rail + searchable right pane) ─────────────
 * Generic shell used by both the Playlist ExpandSheet and the SocialExpandSheet. Selection is
 * controlled by the caller: toggling a row (or a preset's "Add All" CTA) calls onApply with the
 * next id set immediately — same behavior as the inline pickers, no staging or footer. The
 * left rail lists groups (channels / platforms / presets); the right pane lists the active
 * group's items. Everything item-specific (icons, subtitles, hover actions, sorting) comes in
 * through render props so the two sheets stay pixel-identical to their originals. */

export type RailEntry<T> = {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** Items shown in the right pane when this entry is active; also drives the count badge. */
  items: T[];
  /** Optional section heading rendered above this entry (only when it differs from the previous row's). */
  section?: string;
  /**
   * Replace the pane's search box with a CTA of this label that selects every item of the
   * entry (preset behavior: one click adds all).
   */
  stageAllCta?: string;
  disabled?: boolean;
};

type PickerSheetProps<T> = {
  show: boolean;
  /** Ids currently applied by the caller (rendered checked; the sheet never copies this). */
  selected: Set<string>;
  onClose: () => void;
  /** Receives the next id set on every toggle; the caller applies the diff immediately. */
  onApply: (next: Set<string>) => void;
  rail: RailEntry<T>[];
  /** Rail entry to activate when the sheet opens (or once data arrives). */
  defaultActiveKey?: string;
  paneTitle: string;
  searchPlaceholder: string;
  emptySearchText: string;
  emptyText: string;
  getId: (item: T) => string;
  getSearchText: (item: T) => string;
  /** Middle content of an item row (title + subtitle block). */
  renderItemBody: (item: T) => React.ReactNode;
  /** Optional avatar/icon between the check and the body. */
  renderItemLeading?: (item: T) => React.ReactNode;
  /** Leading icon of a Selected-column chip; falls back to renderItemLeading. */
  renderChipLeading?: (item: T) => React.ReactNode;
  /** Optional trailing hover actions (favorite / edit buttons). */
  renderItemActions?: (item: T, ctx: { isHover: boolean }) => React.ReactNode;
  /** Optional reorder of the searched items (e.g. favorites first). */
  sortItems?: (items: T[]) => T[];
  /** Colors of the row check icon (selected / idle). */
  checkColors?: { active: string; idle: string };
  /** Ran each time the sheet opens (fetch presets / favorites). */
  onOpen?: () => void;
  isItemDisabled?: (item: T) => boolean;
  disabledHint?: string;
  computeNext?: (id: string, item: T, selected: Set<string>) => Set<string>;
  /**
   * Enables drag-reorder of the Selected-column chips: the top chip is the primary one, and
   * dropping a different chip on top calls this with its id.
   */
  onChipMakePrimary?: (id: string) => void;
  /** Id of the primary chip; when unset (or no longer selected) the first chip is primary. */
  chipPrimaryId?: string | null;
  /** Replaces the default chip body (used by the playlist sheet to reuse PlaylistChip). */
  renderChip?: (
    item: T,
    ctx: { isPrimary: boolean; remove: () => void }
  ) => React.ReactNode;
};

/** Drag-reorder state for a chip list where the top chip is the primary one. Shared by the
 * PickerSheet Selected column and the inline playlist chip list. Keeps known ids in their
 * dragged order, appends newly selected ids, floats `primaryId` to the top, and calls
 * `onMakePrimary` with the new top id when a drag lands. */
export function useChipDragOrder(
  ids: string[],
  primaryId: string | null | undefined,
  onMakePrimary?: (id: string) => void
) {
  const [order, setOrder] = useState<string[]>([]);
  const [dragId, setDragId] = useState<string | null>(null);
  const orderRef = useRef<string[]>([]);
  orderRef.current = order;

  const idsKey = ids.join("|");
  useEffect(() => {
    setOrder((prev) => {
      let next = prev.filter((id) => ids.includes(id));
      next = [...next, ...ids.filter((id) => !next.includes(id))];
      if (primaryId && next.includes(primaryId))
        next = [primaryId, ...next.filter((id) => id !== primaryId)];
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, primaryId]);

  const dragProps = (id: string) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent<HTMLDivElement>) => {
      setDragId(id);
      e.dataTransfer.effectAllowed = "move";
    },
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!dragId || dragId === id) return;
      setOrder((prev) => {
        const from = prev.indexOf(dragId);
        const to = prev.indexOf(id);
        if (from < 0 || to < 0) return prev;
        const next = [...prev];
        next.splice(from, 1);
        // Removing first shifts the target left when moving down, so inserting at the
        // target's original index lands after it (down) or before it (up).
        next.splice(to, 0, dragId);
        return next;
      });
    },
    onDragEnd: () => {
      setDragId(null);
      const top = orderRef.current[0];
      if (top && top !== primaryId) onMakePrimary?.(top);
    },
  });

  /** Drop zone rendered after the last chip — lets a chip be dragged to the end of the list. */
  const endDropProps = {
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!dragId) return;
      setOrder((prev) =>
        prev[prev.length - 1] === dragId
          ? prev
          : [...prev.filter((x) => x !== dragId), dragId]
      );
    },
  };

  return { order, dragId, dragProps, endDropProps };
}

const PickerSheet = <T,>({
  show,
  selected,
  onClose,
  onApply,
  rail,
  defaultActiveKey,
  paneTitle,
  searchPlaceholder,
  emptySearchText,
  emptyText,
  getId,
  getSearchText,
  renderItemBody,
  renderItemLeading,
  renderChipLeading,
  renderItemActions,
  sortItems,
  checkColors = { active: "#0d6efd", idle: "#adb5bd" },
  onOpen,
  isItemDisabled,
  disabledHint,
  computeNext,
  onChipMakePrimary,
  chipPrimaryId,
  renderChip,
}: PickerSheetProps<T>) => {
  const [activeKey, setActiveKey] = useState<string>("");
  const [hovered, setHovered] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  // Reset the pane each time the sheet opens.
  useEffect(() => {
    if (show) {
      setQuery("");
      setActiveKey(defaultActiveKey || "");
      onOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Adopt the default rail entry once data arrives (platforms may load after open).
  useEffect(() => {
    if (show && !activeKey && defaultActiveKey) setActiveKey(defaultActiveKey);
  }, [show, activeKey, defaultActiveKey]);

  const active = rail.find((r) => r.key === activeKey) ?? rail[0];

  // Items of the active rail entry, narrowed by the search box in the pane header.
  // Entries with a select-all CTA have no search box, so the query never narrows them.
  const visibleItems = useMemo(() => {
    const items = active?.items ?? [];
    const q = active?.stageAllCta ? "" : query.trim().toLowerCase();
    const matched = q
      ? items.filter((it) => getSearchText(it).toLowerCase().includes(q))
      : items;
    return sortItems ? sortItems(matched) : matched;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, query, sortItems]);

  // Immediate apply — same as toggling in the inline picker outside the sheet.
  const toggle = (id: string, item?: T) => {
    if (computeNext && item) {
      onApply(computeNext(id, item, selected));
      return;
    }
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    onApply(next);
  };

  const selectedCount = (items: T[]) =>
    items.filter((it) => selected.has(getId(it))).length;

  const openEntry = (entry: RailEntry<T>) => setActiveKey(entry.key);

  // Selected items in selection (insertion) order, deduped by id (presets repeat channels).
  const selectedItems = useMemo(() => {
    const byIdMap = new Map<string, T>();
    rail.forEach((entry) =>
      entry.items.forEach((it) => {
        const id = getId(it);
        if (!byIdMap.has(id)) byIdMap.set(id, it);
      })
    );
    return [...selected]
      .map((id) => byIdMap.get(id))
      .filter((it): it is T => it !== undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rail, selected]);

  // Drag order of the Selected column (active only when onChipMakePrimary is provided).
  const {
    order: chipOrder,
    dragId,
    dragProps,
    endDropProps,
  } = useChipDragOrder(
    selectedItems.map(getId),
    chipPrimaryId,
    onChipMakePrimary
  );

  // Chips in drag order; the top chip is the primary one.
  const chips = useMemo(() => {
    if (!onChipMakePrimary) return selectedItems;
    const m = new Map(selectedItems.map((it) => [getId(it), it]));
    return chipOrder
      .map((id) => m.get(id))
      .filter((it): it is T => it !== undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, chipOrder]);
  const primaryChipId =
    onChipMakePrimary && chips.length > 0 ? getId(chips[0]) : null;

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      placement="end"
      container={typeof document !== "undefined" ? document.body : undefined}
      style={{ width: 820 }}
    >
      <Offcanvas.Body className="d-flex flex-column p-0">
        <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
          <div className="d-flex flex-grow-1">
            {/* Left rail — groups (channels / platforms / presets) */}
            <div
              className="border-end overflow-auto flex-shrink-0"
              style={{ width: 190 }}
            >
              {rail.map((entry, i) => {
                const isActive = entry.key === active?.key;
                const n = selectedCount(entry.items);
                const hoverKey = `rail:${entry.key}`; // namespaced so a group never collides with an item id
                const heading =
                  entry.section && entry.section !== rail[i - 1]?.section ? (
                    <div
                      className={`px-3 py-2 text-secondary fw-semibold text-uppercase border-bottom bg-light ${
                        i === 0 ? "position-sticky top-0" : "border-top"
                      }`}
                      style={{
                        fontSize: 11,
                        letterSpacing: ".05em",
                        ...(i === 0 ? { zIndex: 2 } : {}),
                      }}
                    >
                      {entry.section}
                    </div>
                  ) : null;
                return (
                  <React.Fragment key={entry.key}>
                    {heading}
                    <div
                      role="button"
                      className={`d-flex align-items-center gap-2 p-3 border-bottom ${
                        isActive
                          ? "bg-primary bg-opacity-10 fw-medium"
                          : hovered === hoverKey
                            ? "bg-light"
                            : ""
                      }`}
                      style={{ cursor: "pointer", fontSize: 13 }}
                      onMouseEnter={() => setHovered(hoverKey)}
                      onMouseLeave={() =>
                        setHovered((h) => (h === hoverKey ? null : h))
                      }
                      onClick={() => openEntry(entry)}
                    >
                      <span className="flex-shrink-0 d-inline-flex">
                        {entry.icon}
                      </span>
                      <span className="text-truncate flex-grow-1">
                        {entry.label}
                      </span>
                      {n > 0 && (
                        <span
                          className="badge rounded-pill bg-primary flex-shrink-0"
                          style={{ fontSize: 9 }}
                        >
                          {n}
                        </span>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Right pane — the active group's items */}
            <div className="flex-grow-1 overflow-auto" style={{ minWidth: 0 }}>
              <div
                className="d-flex align-items-center justify-content-between gap-2 p-3 border-bottom position-sticky top-0 bg-white"
                style={{ zIndex: 2 }}
              >
                <span className="text-muted f-14 fw-medium text-uppercase">
                  {paneTitle}
                </span>
                {active?.stageAllCta ? (
                  // Preset pane: no search — one CTA adding every destination of the preset.
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-shrink-0 position-absolute"
                    style={{ right: "10px" }}
                    disabled={
                      active.items.length === 0 ||
                      active.items.every(
                        (it) =>
                          selected.has(getId(it)) || !!isItemDisabled?.(it)
                      )
                    }
                    onClick={() => {
                      let next = new Set(selected);
                      active.items.forEach((it) => {
                        if (isItemDisabled?.(it)) return;
                        const id = getId(it);
                        if (next.has(id)) return;
                        if (computeNext) {
                          next = computeNext(id, it, next);
                        } else {
                          next.add(id);
                        }
                      });
                      onApply(next);
                    }}
                  >
                    {active.stageAllCta}
                  </Button>
                ) : (
                  <div
                    className="position-absolute"
                    style={{ width: 200, right: "20px" }}
                  >
                    <i
                      className="bi bi-search text-muted position-absolute top-50 translate-middle-y"
                      style={{ left: 8, fontSize: 12 }}
                    />
                    <input
                      type="search"
                      className="form-control form-control-sm"
                      style={{ paddingLeft: 26 }}
                      placeholder={searchPlaceholder}
                      aria-label={searchPlaceholder}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                )}
              </div>
              {visibleItems.length === 0 ? (
                <div className="text-center text-muted small py-4 px-3">
                  {query.trim() ? emptySearchText : emptyText}
                </div>
              ) : (
                visibleItems.map((item) => {
                  const id = getId(item);
                  const isSel = selected.has(id);
                  const isHover = hovered === id;
                  const disabled =
                    !isSel && (!!active?.disabled || !!isItemDisabled?.(item));
                  return (
                    <div
                      key={id}
                      role="button"
                      aria-pressed={isSel}
                      aria-disabled={disabled}
                      title={disabled ? disabledHint : undefined}
                      className={`d-flex align-items-start gap-2 p-3 ${
                        isSel ? "" : isHover ? "bg-light" : ""
                      }`}
                      style={{
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.45 : 1,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                      onMouseEnter={() => !disabled && setHovered(id)}
                      onMouseLeave={() =>
                        setHovered((h) => (h === id ? null : h))
                      }
                      onClick={() => !disabled && toggle(id, item)}
                    >
                      {/* selected → filled check; hover → outline check */}
                      <CheckCircle
                        filled={isSel}
                        className="flex-shrink-0 mt-1"
                        color={isSel ? checkColors.active : checkColors.idle}
                      />
                      {renderItemLeading?.(item)}
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        {renderItemBody(item)}
                      </div>
                      {renderItemActions?.(item, { isHover })}
                    </div>
                  );
                })
              )}
            </div>
          </div>
          {/* Selected column — applied items as removable chips (same chip as the inline sections) */}
          <div
            className="border-start overflow-auto flex-shrink-0"
            style={{ width: 250 }}
          >
            <div
              className="d-flex align-items-center justify-content-between p-3 border-bottom position-sticky top-0 bg-white"
              style={{ zIndex: 2 }}
            >
              <span className="text-muted f-14 fw-medium text-uppercase">
                Selected
                {selectedItems.length > 0 ? ` (${selectedItems.length})` : ""}
              </span>
            </div>
            <div className="d-flex flex-column gap-2 p-3">
              {selectedItems.length === 0 ? (
                <div className="text-muted small">Nothing selected yet.</div>
              ) : (
                chips.map((item) => {
                  const id = getId(item);
                  const isPrimaryChip = id === primaryChipId;
                  const remove = () => toggle(id, item);
                  const dragEnabled = !!onChipMakePrimary;
                  const body = renderChip ? (
                    renderChip(item, { isPrimary: isPrimaryChip, remove })
                  ) : (
                    <div
                      className="social-chip gap-1 p-1"
                      style={{ minWidth: 0 }}
                    >
                      {(renderChipLeading ?? renderItemLeading)?.(item)}
                      <span
                        className="text-truncate flex-grow-1"
                        style={{ fontSize: 13 }}
                      >
                        {getSearchText(item)}
                      </span>
                      <i
                        className="bi bi-x fs-4 flex-shrink-0"
                        role="button"
                        tabIndex={0}
                        aria-label={`Remove ${getSearchText(item)}`}
                        style={{ cursor: "pointer" }}
                        onClick={remove}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            remove();
                          }
                        }}
                      />
                    </div>
                  );
                  return dragEnabled ? (
                    <div
                      key={id}
                      {...dragProps(id)}
                      style={{
                        cursor: "grab",
                        opacity: dragId === id ? 0.5 : 1,
                      }}
                    >
                      {body}
                    </div>
                  ) : (
                    <React.Fragment key={id}>{body}</React.Fragment>
                  );
                })
              )}
              {/* Tail drop zone — gives room to drag a chip below the last one. */}
              {dragId && <div style={{ height: 32 }} {...endDropProps} />}
            </div>
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default PickerSheet;
